import { useState, useEffect, useRef } from 'react';

const references = [
  {
    name: 'Restaurant Gastronomique',
    category: 'Restauration',
    description: 'Installation électrique complète et système de ventilation pour restaurant gastronomique',
    image: 'https://readdy.ai/api/search-image?query=elegant%20luxury%20restaurant%20interior%20with%20modern%20lighting%20fixtures%20sophisticated%20electrical%20installations%20high-end%20dining%20space%20professional%20photography%20clean%20simple%20background%20premium%20ambiance&width=1920&height=1080&seq=restaurant-ref-showcase-001&orientation=landscape',
    icon: 'ri-restaurant-line',
    color: 'from-amber-600/80 to-amber-900/80',
  },
  {
    name: 'Centre Commercial',
    category: 'Commerce',
    description: 'Installations électriques, ventilation et climatisation pour centre commercial',
    image: 'https://readdy.ai/api/search-image?query=modern%20shopping%20mall%20interior%20with%20advanced%20electrical%20systems%20contemporary%20commercial%20architecture%20retail%20space%20professional%20photography%20clean%20simple%20background%20Geneva%20shopping%20center&width=1920&height=1080&seq=centre-commercial-showcase-002&orientation=landscape',
    icon: 'ri-store-2-line',
    color: 'from-teal-600/80 to-teal-900/80',
  },
  {
    name: 'Ville de Genève',
    category: 'Secteur Public',
    description: 'Maintenance et mise aux normes OIBT des installations électriques municipales',
    image: 'https://readdy.ai/api/search-image?query=Geneva%20city%20hall%20official%20government%20building%20modern%20public%20infrastructure%20electrical%20systems%20Swiss%20architecture%20professional%20photography%20clean%20simple%20background%20administrative%20building&width=1920&height=1080&seq=ville-geneve-showcase-003&orientation=landscape',
    icon: 'ri-government-line',
    color: 'from-slate-600/80 to-slate-900/80',
  },
  {
    name: 'Appartements Haut de Gamme',
    category: 'Résidentiel',
    description: 'Installations électriques, domotique et chauffage pour appartements haut de gamme',
    image: 'https://readdy.ai/api/search-image?query=luxury%20modern%20apartment%20interior%20with%20elegant%20lighting%20smart%20home%20automation%20high-end%20residential%20electrical%20installations%20contemporary%20design%20professional%20photography%20clean%20simple%20background%20premium%20living%20space&width=1920&height=1080&seq=appartement-showcase-004&orientation=landscape',
    icon: 'ri-building-line',
    color: 'from-rose-600/80 to-rose-900/80',
  },
  {
    name: 'Villas Privées',
    category: 'Résidentiel Premium',
    description: 'Solutions CVSE complètes pour villas de prestige avec pompes à chaleur et domotique',
    image: 'https://readdy.ai/api/search-image?query=modern%20luxury%20villa%20exterior%20with%20sophisticated%20outdoor%20lighting%20premium%20residential%20property%20elegant%20architecture%20professional%20photography%20clean%20simple%20background%20high-end%20home%20Geneva%20style&width=1920&height=1080&seq=villa-showcase-005&orientation=landscape',
    icon: 'ri-home-heart-line',
    color: 'from-emerald-600/80 to-emerald-900/80',
  },
  {
    name: 'Bâtiments Commerciaux',
    category: 'Commercial',
    description: 'Installations électriques industrielles, ventilation et systèmes de sécurité',
    image: 'https://readdy.ai/api/search-image?query=modern%20commercial%20office%20building%20with%20advanced%20electrical%20systems%20contemporary%20architecture%20business%20infrastructure%20professional%20photography%20clean%20simple%20background%20corporate%20real%20estate%20Geneva&width=1920&height=1080&seq=batiment-showcase-006&orientation=landscape',
    icon: 'ri-building-4-line',
    color: 'from-cyan-600/80 to-cyan-900/80',
  },
  {
    name: 'Immeubles Résidentiels',
    category: 'Immobilier',
    description: 'Rénovation complète des installations CVSE et mise en conformité NIBT',
    image: 'https://readdy.ai/api/search-image?query=modern%20residential%20apartment%20building%20with%20quality%20electrical%20installations%20contemporary%20Swiss%20architecture%20multi-family%20housing%20professional%20photography%20clean%20simple%20background%20urban%20living%20Geneva&width=1920&height=1080&seq=immeuble-showcase-007&orientation=landscape',
    icon: 'ri-community-line',
    color: 'from-violet-600/80 to-violet-900/80',
  },
];

const SLIDE_DURATION = 4000;

export default function ReferencesShowcase() {
  const [current, setCurrent] = useState(0);
  const [prev, setPrev] = useState<number | null>(null);
  const [animating, setAnimating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const progressRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const goTo = (index: number) => {
    if (animating || index === current) return;
    setAnimating(true);
    setPrev(current);
    setCurrent(index);
    setProgress(0);
    setTimeout(() => {
      setPrev(null);
      setAnimating(false);
    }, 700);
  };

  const next = () => goTo((current + 1) % references.length);
  const goToPrev = () => goTo((current - 1 + references.length) % references.length);

  useEffect(() => {
    if (paused) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (progressRef.current) clearInterval(progressRef.current);
      return;
    }

    setProgress(0);
    progressRef.current = setInterval(() => {
      setProgress(p => {
        if (p >= 100) return 100;
        return p + (100 / (SLIDE_DURATION / 50));
      });
    }, 50);

    intervalRef.current = setInterval(() => {
      setCurrent(c => {
        const next = (c + 1) % references.length;
        setPrev(c);
        setAnimating(true);
        setProgress(0);
        setTimeout(() => {
          setPrev(null);
          setAnimating(false);
        }, 700);
        return next;
      });
    }, SLIDE_DURATION);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (progressRef.current) clearInterval(progressRef.current);
    };
  }, [paused, current]);

  const ref = references[current];
  const prevRef = prev !== null ? references[prev] : null;

  return (
    <section className="relative w-full bg-black overflow-hidden" style={{ height: '90vh', minHeight: '600px' }}>
      {/* Background slides */}
      {prevRef && (
        <div
          className="absolute inset-0 z-10 transition-opacity duration-700"
          style={{ opacity: animating ? 0 : 1 }}
        >
          <img
            src={prevRef.image}
            alt={prevRef.name}
            className="w-full h-full object-cover object-top"
          />
          <div className={`absolute inset-0 bg-gradient-to-br ${prevRef.color}`} />
          <div className="absolute inset-0 bg-black/30" />
        </div>
      )}

      <div
        className="absolute inset-0 z-20 transition-opacity duration-700"
        style={{ opacity: animating ? 1 : 1 }}
      >
        <img
          src={ref.image}
          alt={ref.name}
          className="w-full h-full object-cover object-top"
          style={{
            animation: 'kenburns 8s ease-in-out infinite alternate',
          }}
        />
        <div className={`absolute inset-0 bg-gradient-to-br ${ref.color}`} />
        <div className="absolute inset-0 bg-black/30" />
      </div>

      {/* Ken Burns keyframes */}
      <style>{`
        @keyframes kenburns {
          from { transform: scale(1) translateX(0); }
          to { transform: scale(1.08) translateX(-1%); }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .slide-up { animation: slideUp 0.7s ease forwards; }
      `}</style>

      {/* Content */}
      <div className="relative z-30 h-full flex flex-col justify-between px-8 md:px-16 lg:px-24 py-12">
        {/* Top bar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-white/60 text-sm font-medium tracking-widest uppercase">Nos Références</span>
            <span className="w-8 h-px bg-white/40"></span>
            <span className="text-white/40 text-sm">{current + 1} / {references.length}</span>
          </div>
          <button
            onClick={() => setPaused(p => !p)}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-all cursor-pointer"
          >
            <i className={paused ? 'ri-play-line' : 'ri-pause-line'}></i>
          </button>
        </div>

        {/* Center content */}
        <div className="flex flex-col lg:flex-row items-end justify-between gap-8">
          <div className="max-w-2xl" key={current}>
            <span className="slide-up inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 border border-white/20 text-white text-xs font-semibold tracking-wider uppercase mb-5 backdrop-blur-sm">
              <i className={`${ref.icon} text-sm`}></i>
              {ref.category}
            </span>
            <h2 className="slide-up text-4xl md:text-6xl font-black text-white leading-tight mb-4" style={{ animationDelay: '0.1s' }}>
              {ref.name}
            </h2>
            <p className="slide-up text-white/70 text-lg leading-relaxed max-w-xl" style={{ animationDelay: '0.2s' }}>
              {ref.description}
            </p>
          </div>

          {/* Thumbnail strip */}
          <div className="flex gap-3 flex-shrink-0">
            {references.map((r, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className={`relative overflow-hidden rounded-xl transition-all duration-300 cursor-pointer flex-shrink-0 ${
                  i === current
                    ? 'w-24 h-16 md:w-32 md:h-20 ring-2 ring-white'
                    : 'w-14 h-10 md:w-20 md:h-14 opacity-50 hover:opacity-80'
                }`}
              >
                <img src={r.image} alt={r.name} className="w-full h-full object-cover object-top" />
                {i === current && (
                  <div className="absolute bottom-0 left-0 h-0.5 bg-white transition-all" style={{ width: `${progress}%` }} />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Bottom controls */}
        <div className="flex items-center justify-between">
          {/* Dots */}
          <div className="flex gap-2">
            {references.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className={`transition-all duration-300 rounded-full cursor-pointer ${
                  i === current ? 'w-8 h-2 bg-white' : 'w-2 h-2 bg-white/30 hover:bg-white/60'
                }`}
              />
            ))}
          </div>

          {/* Arrows */}
          <div className="flex gap-3">
            <button
              onClick={goToPrev}
              className="w-12 h-12 flex items-center justify-center rounded-full bg-white/10 border border-white/20 text-white hover:bg-white/25 transition-all cursor-pointer"
            >
              <i className="ri-arrow-left-line text-lg"></i>
            </button>
            <button
              onClick={next}
              className="w-12 h-12 flex items-center justify-center rounded-full bg-white text-black hover:bg-white/90 transition-all cursor-pointer"
            >
              <i className="ri-arrow-right-line text-lg"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
